import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Box,
  Badge,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  DialogContent,
  Avatar,
  IconButton,
  LinearProgress,
  Snackbar,
  AppBar,
  CardMedia
} from "@material-ui/core";
import { UserContext } from "../../../../../UserProvider";
import { Store } from "../../../../../models/Store";
import { useI18n } from "../../../../../hooks/useI18n";
import { Inventory } from "@mui/icons-material";
import useStyles from "../styles";
import { globalSettings } from "../../../../../models/Settings";
import InfiniteScroll from "react-infinite-scroll-component";
import { Lock } from "@material-ui/icons";
import { HubConnectionBuilder } from "@microsoft/signalr";
import {
  getLockedItems,
  getProcessedItems,
  lockItem,
  processItem
} from "../../../../../api/stocktakingApi";
import { isOk } from "../../../../../result";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../store";
import { thunkFetchProductsV2 } from "../../../../../store/products/action";
import { thunkFetchAdmins } from "../../../../../store/users/actions";
import { Person } from "../../../../../models/Person";
import {
  getProductConcatName,
  StoreStocktakingItem
} from "../../../../../models/Stocktaking";
import { Authenticated } from "../../../../../models/User";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { Alert } from "@material-ui/lab";
import Toolbar from "@material-ui/core/Toolbar";
import { Product } from "../../../../../models/Product";
import { ListProcessedItems } from "./ListProcessedItems";
import { getSingleV2 } from "../../../../../api/productApi";
import { ScanditBarcodeScanner } from "../../../../../components/ScanditBarcodeScanner";

export interface RunStocktakingProps {
  store: Store;
  stocktakingListInit: StoreStocktakingItem[];
}

export const RunStocktaking = ({
  store,
  stocktakingListInit
}: RunStocktakingProps) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useContext(UserContext) as Authenticated;
  const classes = useStyles();
  const { t } = useI18n();
  const dispatch = useDispatch();

  const administrators =
    useSelector<AppState, ReadonlyArray<Person>>(
      s => s.users.admins as ReadonlyArray<Person>
    ) || [];

  const products =
    useSelector<AppState, ReadonlyArray<Product>>(
      s => s.storeClusterProducts.productState.products
    ) || [];

  const [lockedItems, setLockedItems, getLockedItemsState] = useExtendedState(
    [] as any
  );
  const [processedItems, setProcessedItems, getProcessedItemsState] =
    useExtendedState([] as any);
  const [showProcessedDialog, setShowProcessedDialog] = useState(false);
  const [animateProcessed, setAnimateProcessed] = useState(false);
  const [pauseScan, setPauseScan] = useState(false);

  const [state, setState] = useState({
    isLoading: false,
    postIsLoading: false,
    lockIsLoadingItem: "",
    showError: false,
    showSuccess: false,
    showLockedError: { display: false, item: {} as any },
    showProcessedError: { display: false, item: {} as any },
    stockTakingList: stocktakingListInit,
    showing: stocktakingListInit.slice(0, 50),
    pageEnd: 50,
    activeItem: {} as any,
    totalStocktakingItems: 0
  });

  //HACK TO GET CURRENT STATE FROM SCANDIT ONSCAN
  function useExtendedState<T>(initialState: T) {
    const [state, setState] = React.useState<T>(initialState);
    const getLatestState = () => {
      return new Promise<T>((resolve, reject) => {
        setState(s => {
          resolve(s);
          return s;
        });
      });
    };

    return [state, setState, getLatestState] as const;
  }

  useEffect(() => {
    if (!products || !products.length) {
      dispatch(thunkFetchProductsV2());
    }
    if (!administrators || !administrators.length) {
      dispatch(thunkFetchAdmins());
    }
  }, []);

  useEffect(() => {
    const getUpdatedItemList = async () => {
      setState({ ...state, isLoading: true });
      const result = await getLockedItems(store.id);
      if (isOk(result)) {
        setLockedItems(result.value);
      }

      const processedResult = await getProcessedItems(
        store.id,
        stocktakingListInit[0].created
      );

      let totalProcessed = 0;
      if (isOk(processedResult)) {
        setProcessedItems(processedResult.value);
        totalProcessed = processedResult.value.length;
      }

      setState({
        ...state,
        totalStocktakingItems: stocktakingListInit.length + totalProcessed,
        isLoading: false
      });
    };

    const createSignalRConnection = async () => {
      const signalRconnection = new HubConnectionBuilder()
        .withUrl("/hubs/stock-taking")
        .build();
      try {
        await signalRconnection.start();

        signalRconnection.on(`LockedItems`, items => {
          setLockedItems(items);
        });

        signalRconnection.on(`ProcessedItems`, items => {
          setProcessedItems(items);
          setAnimateProcessed(true);
          setTimeout(() => setAnimateProcessed(false), 1000);
        });

        await signalRconnection.invoke("SubscribeToStoreStocktaking", store.id);
      } catch (err) {
        console.log(err);
      }
    };

    createSignalRConnection();
    getUpdatedItemList();

    return () => {
      //Unlock all current user items on exit
      lockCurrentItem({} as any, false);
    };
  }, []);

  useEffect(() => {
    if (state.activeItem?.product?.gtin != undefined) {
      setPauseScan(true);
    } else {
      setPauseScan(false);
    }
  }, [state.activeItem]);

  const getNextItems = (start: number, end: number) => {
    return state.stockTakingList.slice(
      start,
      end > state.stockTakingList.length ? state.stockTakingList.length : end
    );
  };

  const handleFetchMore = () => {
    const currentItems = state.showing;
    const nextItems = getNextItems(state.pageEnd, state.pageEnd + 50);

    const mergedItems = currentItems.concat(nextItems);

    setState({
      ...state,
      pageEnd: state.pageEnd + 50,
      showing: mergedItems
    });
  };

  const getAdmin = (lastUpdatedBy: string): Person => {
    const person = administrators.find((a: Person) => a.id === lastUpdatedBy);
    return person ?? ({} as Person);
  };

  const processBarcode = async (barcode: string) => {
    const processedItemsState = await getProcessedItemsState();
    const processedItem = processedItemsState.find(
      (x: any) => x.gtin === barcode
    );
    if (processedItem !== undefined) {
      debugger;
      setState({
        ...state,
        showProcessedError: { display: true, item: processedItem },
        activeItem: {}
      });
      return;
    }

    const lockedItemsState = await getLockedItemsState();
    const lockedItem = lockedItemsState.find(
      (x: any) => x.gtin === barcode && x.lastUpdatedBy !== user.id
    );

    if (lockedItem !== undefined) {
      setState({
        ...state,
        showLockedError: { display: true, item: lockedItem },
        activeItem: {}
      });
      return;
    }

    const item = state.stockTakingList.find(
      (x: any) => x.product?.gtin == barcode
    );
    if (item == null) {
      setState({
        ...state,
        showProcessedError: { display: true, item: {} as any },
        activeItem: {}
      });
      return;
    }

    lockCurrentItem(item, true);
  };

  const lockCurrentItem = (item: StoreStocktakingItem, lock: boolean) => {
    setState({ ...state, lockIsLoadingItem: item.id });
    lockItem(store.id, item.id, lock).then(result => {
      if (isOk(result)) {
        const p = products.find((x: any) => x.id === item.product.id);
        setState({
          ...state,
          activeItem: lock ? { ...item, imageUrl: p?.imageUrl } : ({} as any),
          showLockedError: { display: false, item: {} as any },
          showProcessedError: { display: false, item: {} as any },
          showError: false,
          lockIsLoadingItem: ""
        });
      } else {
        setState({ ...state, showError: true, lockIsLoadingItem: "" });
      }
    });
  };

  const submitOnShelf = async (event: any) => {
    if (state.activeItem.onShelf === undefined) return;
    setState({ ...state, postIsLoading: true });
    const result = await processItem(store.id, state.activeItem);

    if (isOk(result)) {
      const nextItems = getNextItems(state.pageEnd, state.pageEnd + 2);

      const mergedItems = state.showing.concat(nextItems);

      setState({
        ...state,
        activeItem: {} as any,
        postIsLoading: false,
        showSuccess: true,
        showError: false,
        showing: mergedItems,
        pageEnd: state.pageEnd + 2
      });
    } else {
      setState({
        ...state,
        activeItem: {} as any,
        postIsLoading: false,
        showSuccess: false,
        showError: true
      });
    }
  };

  const getInputAlert = () => {
    if (state.showProcessedError?.display) {
      const admin = getAdmin(state.showProcessedError?.item?.lastUpdatedBy);
      return admin?.name === undefined ? (
        <Alert severity={"error"}>
          {`Barcode ${state.showProcessedError?.item?.gtin} missing in current stocktaking.`}
        </Alert>
      ) : (
        <Alert
          severity={"warning"}
        >{`Barcode ${state.showProcessedError?.item?.gtin} has been processed by ${admin?.name}.`}</Alert>
      );
    }

    if (state.showLockedError?.display) {
      const admin = getAdmin(state.showLockedError?.item?.lastUpdatedBy);
      return (
        <Alert
          severity={"warning"}
        >{`Barcode ${state.showLockedError?.item?.gtin} is locked by ${admin?.name}.`}</Alert>
      );
    }

    if (state.showError) {
      return (
        <Alert severity={"error"}>
          {t("common.messages.failedToUpdateOrLockStocktaking")}
        </Alert>
      );
    }

    return (
      <Alert severity={"info"}>
        Scan a product or click on one in the list
      </Alert>
    );
  };

  return state.isLoading ? (
    <div className={classes.spinnerContainer}>
      <CircularProgress />
    </div>
  ) : (
    <>
      <div
        className={classes.scanner}
        style={
          mobile
            ? {
                display: "flex",
                height: 200,
                width: "100%"
              }
            : {}
        }
      >
        <ScanditBarcodeScanner
          licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
          libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
          onScan={async (data: string) => {
            if (data) {
              await processBarcode(data);
            }
          }}
        />
      </div>
      <Box sx={{ padding: 10, paddingBottom: 0 }}>{getInputAlert()}</Box>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.activeItem.product?.gtin != undefined}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box sx={{ padding: 20, paddingBottom: 0 }}>
          {state.activeItem.product?.gtin && (
            <>
              <Box mb={"2rem"} sx={{ height: 400 }}>
                <CardMedia
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain"
                  }}
                  component={"img"}
                  image={state.activeItem.imageUrl}
                  title={state.activeItem.product.name}
                />
              </Box>
              <Typography
                variant={"subtitle1"}
                style={{
                  lineHeight: "1.4rem"
                }}
              >
                {getProductConcatName(state.activeItem.product)}
              </Typography>
              <Typography variant={"subtitle2"} color={"textSecondary"}>
                {state.activeItem.product?.gtin}
              </Typography>
            </>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 5,
              marginTop: 5
            }}
          >
            <TextField
              inputRef={input => {
                if (input != null) {
                  if (state.activeItem.product?.gtin) {
                    input.focus();
                  } else input.blur();
                }
              }}
              id={"inputOnShelf"}
              style={{ flex: 1 }}
              type={"number"}
              inputProps={{ inputMode: "numeric" }}
              className={classes.field}
              label={t("common.formFields.onShelf")}
              value={state.activeItem.onShelf ?? ""}
              inputMode={"numeric"}
              onKeyDown={event => {
                if (event.key === "Enter") submitOnShelf(event);
                return;
              }}
              onChange={(e: any) => {
                setState({
                  ...state,
                  activeItem: {
                    ...state.activeItem,
                    onShelf: Number(e.target.value)
                  }
                });
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">st</InputAdornment>
              }}
            />
            <Button
              disabled={
                !state.activeItem.product?.gtin ||
                state.activeItem.onShelf === undefined ||
                state.postIsLoading
              }
              variant="contained"
              size={"small"}
              color="primary"
              style={{ marginLeft: 10, marginRight: 10 }}
              onClick={submitOnShelf}
            >
              Save
            </Button>
            <Button
              disabled={!state.activeItem.product?.gtin || state.postIsLoading}
              size={"small"}
              variant="contained"
              onClick={() => {
                lockCurrentItem(state.activeItem, false);
              }}
            >
              Cancel
            </Button>
          </div>
          {state.postIsLoading && (
            <LinearProgress className={classes.divider} style={{ height: 1 }} />
          )}
        </Box>
      </Dialog>

      <List>
        <InfiniteScroll
          dataLength={state.showing.length}
          next={handleFetchMore}
          height={500}
          hasMore={state.stockTakingList.length > state.pageEnd + 50}
          loader={<ListItem key="loading">Loading...</ListItem>}
        >
          {state.showing.map((x: any) => {
            if (!x.OnShelf && !processedItems.some((y: any) => y.id === x.id)) {
              const lockedItem = lockedItems.find((l: any) => l.id === x.id);
              const admin = getAdmin(lockedItem?.lastUpdatedBy);
              const lockedByMe = lockedItem && user.id === admin.id;
              return (
                <ListItem
                  key={x.id}
                  onClick={() => {
                    if (lockedItem && !lockedByMe) return;
                    lockCurrentItem(x, true);
                  }}
                  style={{
                    cursor: lockedItem && !lockedByMe ? "default" : "pointer"
                  }}
                >
                  <Grid container>
                    <Grid item xs={8}>
                      <ListItemText
                        primary={getProductConcatName(x.product)}
                        secondary={x.product.gtin}
                      />
                    </Grid>
                    <Grid item xs={4} style={{ textAlign: "right" }}>
                      {!lockedItem && state.lockIsLoadingItem === x.id && (
                        <CircularProgress size={20} />
                      )}
                      {lockedItem && (
                        <>
                          <Lock
                            style={{
                              color: lockedByMe
                                ? theme.palette.primary.dark
                                : theme.palette.warning.dark
                            }}
                          />
                          <Typography
                            variant={"body2"}
                            style={{ color: theme.palette.grey.A200 }}
                          >
                            {admin?.name}
                          </Typography>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider className={classes.divider} />
                    </Grid>
                  </Grid>
                </ListItem>
              );
            }
          })}
        </InfiniteScroll>
      </List>
      <AppBar
        position={mobile ? "fixed" : "absolute"}
        color={"transparent"}
        style={{
          top: "auto",
          bottom: 0,
          backgroundColor: theme.palette.grey["100"],
          borderTop: `1px solid ${theme.palette.grey.A200}`
        }}
      >
        <Toolbar style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <IconButton
              className={
                animateProcessed ? classes.inventoryProcessedButton : ""
              }
              onClick={() => {
                setShowProcessedDialog(true);
              }}
            >
              <Badge
                badgeContent={processedItems?.length}
                max={999}
                color={"error"}
              >
                <Inventory color={animateProcessed ? "success" : "inherit"} />
              </Badge>
            </IconButton>
            <Typography variant={"body1"}>
              {t("common.messages.processedItems")}
            </Typography>
          </div>
          <Typography variant={"body1"}>
            Left to process:{" "}
            <strong>
              {state.totalStocktakingItems - (processedItems?.length ?? 0)}
            </strong>
          </Typography>
        </Toolbar>
      </AppBar>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        open={showProcessedDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle
          disableTypography
          style={{ display: "flex", alignItems: "center", paddingBottom: 15 }}
        >
          <Avatar
            style={{
              backgroundColor: theme.palette.success.dark,
              display: "inline-flex",
              marginRight: 10
            }}
          >
            <Inventory />
          </Avatar>
          <Typography variant={"h6"} display={"inline"}>
            Processed items
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setShowProcessedDialog(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent
          style={
            mobile
              ? { padding: 0, marginTop: 15 }
              : { marginTop: 15, marginBottom: 20 }
          }
        >
          <ListProcessedItems
            processedItems={processedItems}
            products={products}
            administrators={administrators}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={state.showSuccess}
        autoHideDuration={2000}
        onClose={() => {
          setState({ ...state, showSuccess: false });
        }}
      >
        <Alert
          onClose={() => setState({ ...state, showSuccess: false })}
          severity="success"
        >
          Stocktaking updated!
        </Alert>
      </Snackbar>
    </>
  );
};
